import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { getCourseById } from '../../api/TrainerAPI';
import './styles/CoursePage.css';

// Mock progress data — replace with real data later
const MOCK_MODULE_PROGRESS = {};

const generateMockProgress = (modules) => {
    if (!modules) return {};
    const progress = {};
    const mockValues = [65, 30, 90, 45, 10, 80, 55];
    modules.forEach((m, i) => {
        progress[m.id] = mockValues[i % mockValues.length];
    });
    return progress;
};

const CoursePage = () => {
    const { trainerId, courseId } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedModuleId, setSelectedModuleId] = useState(null);
    const [expandedChapters, setExpandedChapters] = useState({});
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const contentRef = useRef(null);

    const moduleProgress = useMemo(() => {
        if (!course?.modules) return MOCK_MODULE_PROGRESS;
        return generateMockProgress(course.modules);
    }, [course?.modules]);

    const totalProgress = useMemo(() => {
        const values = Object.values(moduleProgress);
        if (values.length === 0) return 0;
        return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    }, [moduleProgress]);

    useEffect(() => {
        if (trainerId && courseId) {
            loadCourse();
        }
    }, [trainerId, courseId]);

    const loadCourse = async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await getCourseById(trainerId, courseId);
            if (!data) throw new Error('Course not found');
            setCourse(data);
            if (data.modules?.length > 0) {
                setSelectedModuleId(data.modules[0].id);
            }
        } catch (err) {
            setError(err?.code === 'not-found' ? 'Course not found' : 'Failed to load course');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const selectedModule = course?.modules?.find(m => m.id === selectedModuleId);

    const toggleChapter = (chapterId) => {
        setExpandedChapters(prev => ({
            ...prev,
            [chapterId]: !prev[chapterId]
        }));
    };

    const selectModule = (moduleId) => {
        setSelectedModuleId(moduleId);
        setExpandedChapters({});
        setSidebarOpen(false);
        contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getChapterStats = (chapter) => {
        const lessonCount = chapter.lessons?.length || 0;
        let videoCount = 0;
        chapter.lessons?.forEach(lesson => {
            videoCount += lesson.videoLinks?.length || 0;
        });
        return { lessonCount, videoCount };
    };

    const renderContentBlocks = (contentBlocks) => {
        if (!contentBlocks || !Array.isArray(contentBlocks) || contentBlocks.length === 0) {
            return <p className="cp-no-content">No content available</p>;
        }
        return (
            <div className="cp-blocks">
                {contentBlocks.map((block) => {
                    if (block.type === 'text') {
                        return <div key={block.id} className="cp-text-block" dangerouslySetInnerHTML={{ __html: block.value }} />;
                    }
                    if (block.type === 'image' && block.url) {
                        return (
                            <div key={block.id} className="cp-image-block">
                                <img src={block.url} alt="Lesson content" loading="lazy" />
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="course-page">
                <div className="cp-center-state">
                    <div className="cp-spinner"></div>
                    <p>Loading course...</p>
                </div>
            </div>
        );
    }

    if (error || !course) {
        return (
            <div className="course-page">
                <div className="cp-center-state">
                    <p>{error || 'Course not found'}</p>
                    <button className="cp-btn" onClick={() => navigate(-1)}>Go Back</button>
                </div>
            </div>
        );
    }

    return (
        <div className="course-page">
            {/* Top Bar */}
            <header className="cp-header">
                <button className="cp-back-btn" onClick={() => navigate(-1)}>
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                </button>
                <h1 className="cp-title">{course.title}</h1>
                <button className="cp-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                    <MenuBookIcon sx={{ fontSize: 20 }} />
                </button>
            </header>

            {/* Sticky Breadcrumbs */}
            <nav className="cp-breadcrumbs">
                <span className={`cp-crumb ${!selectedModule ? 'cp-crumb--active' : 'cp-crumb--link'}`}
                    onClick={() => { setSelectedModuleId(null); setExpandedChapters({}); }}>
                    {course.title}
                </span>
                {selectedModule && (
                    <>
                        <ChevronRightIcon sx={{ fontSize: 16 }} />
                        <span className="cp-crumb cp-crumb--active">{selectedModule.title}</span>
                    </>
                )}
            </nav>

            <div className="cp-layout">
                {/* Sidebar */}
                <aside className={`cp-sidebar ${sidebarOpen ? 'cp-sidebar--open' : ''}`}>
                    {/* Total Progress */}
                    <div className="cp-total-progress">
                        <div className="cp-total-progress__ring">
                            <svg viewBox="0 0 80 80">
                                <circle cx="40" cy="40" r="34" className="cp-ring-bg" />
                                <circle cx="40" cy="40" r="34"
                                    className="cp-ring-fill"
                                    strokeDasharray={`${2 * Math.PI * 34}`}
                                    strokeDashoffset={`${2 * Math.PI * 34 * (1 - totalProgress / 100)}`}
                                />
                            </svg>
                            <span className="cp-total-progress__value">{totalProgress}%</span>
                        </div>
                        <span className="cp-total-progress__label">Course Progress</span>
                    </div>

                    <h2 className="cp-sidebar-title">Modules</h2>
                    <ul className="cp-module-list">
                        {course.modules?.map(module => {
                            const pct = moduleProgress[module.id] || 0;
                            return (
                                <li key={module.id}>
                                    <button
                                        className={`cp-module-btn ${selectedModuleId === module.id ? 'cp-module-btn--active' : ''}`}
                                        onClick={() => selectModule(module.id)}
                                    >
                                        <span className="cp-module-progress-dot">
                                            <svg viewBox="0 0 20 20" width="20" height="20">
                                                <circle cx="10" cy="10" r="8" className="cp-dot-bg" />
                                                <circle cx="10" cy="10" r="8"
                                                    className="cp-dot-fill"
                                                    strokeDasharray={`${2 * Math.PI * 8}`}
                                                    strokeDashoffset={`${2 * Math.PI * 8 * (1 - pct / 100)}`}
                                                />
                                            </svg>
                                        </span>
                                        {module.title}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    {(!course.modules || course.modules.length === 0) && (
                        <p className="cp-empty-msg">No modules</p>
                    )}
                </aside>

                {sidebarOpen && <div className="cp-overlay" onClick={() => setSidebarOpen(false)} />}

                {/* Content */}
                <main className="cp-content" ref={contentRef}>
                    {!selectedModule ? (
                        <div className="cp-welcome">
                            <MenuBookIcon sx={{ fontSize: 48, color: 'rgba(79,111,82,0.4)' }} />
                            <h2>{course.title}</h2>
                            {course.description && <p>{course.description}</p>}
                            <p className="cp-hint">Select a module from the sidebar to begin.</p>
                        </div>
                    ) : (
                        <div className="cp-module-content">
                            <h2 className="cp-module-heading">{selectedModule.title}</h2>

                            {selectedModule.chapters?.length > 0 ? (
                                <div className="cp-chapter-cards">
                                    {selectedModule.chapters.map(chapter => {
                                        const { lessonCount, videoCount } = getChapterStats(chapter);
                                        const isExpanded = expandedChapters[chapter.id];
                                        return (
                                            <div key={chapter.id} className={`cp-chapter-card ${isExpanded ? 'cp-chapter-card--open' : ''}`}>
                                                <button
                                                    className="cp-chapter-card__header"
                                                    onClick={() => toggleChapter(chapter.id)}
                                                >
                                                    <span className="cp-chapter-card__title">{chapter.title}</span>
                                                    <div className="cp-chapter-card__meta">
                                                        <span className="cp-chapter-card__stat">
                                                            <AutoStoriesIcon sx={{ fontSize: 16 }} />
                                                            {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
                                                        </span>
                                                        {videoCount > 0 && (
                                                            <span className="cp-chapter-card__stat">
                                                                <PlayCircleOutlineIcon sx={{ fontSize: 16 }} />
                                                                {videoCount} {videoCount === 1 ? 'video' : 'videos'}
                                                            </span>
                                                        )}
                                                        {isExpanded
                                                            ? <ExpandLessIcon sx={{ fontSize: 20 }} />
                                                            : <ExpandMoreIcon sx={{ fontSize: 20 }} />
                                                        }
                                                    </div>
                                                </button>

                                                {isExpanded && (
                                                    <div className="cp-lessons">
                                                        {chapter.lessons?.length > 0 ? (
                                                            chapter.lessons.map(lesson => (
                                                                <div key={lesson.id} className="cp-lesson">
                                                                    <h4 className="cp-lesson-title">{lesson.title}</h4>
                                                                    {renderContentBlocks(lesson.contentBlocks)}
                                                                    {lesson.videoLinks?.length > 0 && (
                                                                        <div className="cp-video-links">
                                                                            <h5>Video Resources</h5>
                                                                            {lesson.videoLinks.map((link, idx) => (
                                                                                <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="cp-video-link">
                                                                                    {link.title || link.url}
                                                                                </a>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p className="cp-empty-msg">No lessons in this chapter</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="cp-empty-msg">No chapters in this module</p>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default CoursePage;
